import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Autocomplete,
    Chip
} from '@mui/material';
import { FormField } from './sharedStyles';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { getManagers } from '../../redux/slices/users';
import { getReportUsers } from '../../redux/ducks/reports';

const FirstFormField = styled(FormField)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const AssignedUsersHelper = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    display: 'block',
}));

const DepartmentDialog = ({ open, onClose, onSave, department, mode = 'create' }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        email: '',
        manager: '',
        users: [],
    });
    const [managers, setManagers] = useState([]);
    const [reportUsers, setReportUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        if (open) {
            dispatch(getManagers({ callback: (data) => {
                setManagers(data || []);
            }}));
            dispatch(getReportUsers((data) => {
                setReportUsers(data || []);
            }));
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (department && mode === 'edit') {
            setFormData({
                name: department.name || '',
                description: department.description || '',
                email: department.email || '',
                manager: department.manager?._id || department.manager || '',
                users: department.users?.map(u => u._id || u) || [],
            });

            if (reportUsers.length > 0 && department.users) {
                const userIds = department.users.map(u => u._id || u);
                const users = reportUsers.filter(u => userIds.includes(u._id));
                setSelectedUsers(users);
            }
        } else {
            setFormData({
                name: '',
                description: '',
                email: '',
                manager: '',
                users: [],
            });
            setSelectedUsers([]);
        }
    }, [department, mode, open, reportUsers]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUsersChange = (newValue) => {
        setSelectedUsers(newValue);
        setFormData(prev => ({
            ...prev,
            users: newValue.map(user => user._id)
        }));
    };

    const handleSave = () => {
        const dataToSave = {
            name: formData.name,
            description: formData.description,
            email: formData.email,
            manager: formData.manager || undefined,
            users: formData.users,
        };
        onSave(dataToSave);
        handleClose();
    };

    const handleClose = () => {
        setFormData({
            name: '',
            description: '',
            email: '',
            manager: '',
            users: [],
        });
        setSelectedUsers([]);
        onClose();
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValid = () => {
        return formData.name.trim() !== '' &&
               formData.email.trim() !== '' &&
               isValidEmail(formData.email);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{mode === 'create' ? 'Create' : 'Edit'} Department</DialogTitle>
            <DialogContent>
                <FirstFormField>
                    <TextField
                        fullWidth
                        label="Department Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        helperText="e.g., Engineering, Marketing, Sales"
                    />
                </FirstFormField>

                <FormField>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        helperText="Optional description of this department"
                    />
                </FormField>

                <FormField>
                    <TextField
                        fullWidth
                        type="email"
                        label="Department Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={formData.email !== '' && !isValidEmail(formData.email)}
                        helperText={
                            formData.email !== '' && !isValidEmail(formData.email)
                                ? 'Please enter a valid email address'
                                : 'Required for department communications'
                        }
                    />
                </FormField>

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Department Manager</InputLabel>
                        <Select
                            name="manager"
                            value={formData.manager}
                            onChange={handleChange}
                            label="Department Manager"
                        >
                            <MenuItem value="">None</MenuItem>
                            {managers.map(user => (
                                <MenuItem key={user._id} value={user._id}>
                                    {user.fullName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    <Typography variant="subtitle2" gutterBottom>
                        Assigned Users
                    </Typography>
                    <Autocomplete
                        multiple
                        options={reportUsers}
                        getOptionLabel={(user) => user.fullName}
                        value={selectedUsers}
                        onChange={(e, newValue) => handleUsersChange(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select users to assign to this department"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((user, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={user._id}
                                        label={user.fullName}
                                        {...tagProps}
                                    />
                                );
                            })
                        }
                    />
                    <AssignedUsersHelper variant="caption" color="text.secondary">
                        {formData.users.length} user{formData.users.length !== 1 ? 's' : ''} assigned
                    </AssignedUsersHelper>
                </FormField>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!isValid()}>
                    {mode === 'create' ? 'Create' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DepartmentDialog;
